import { authService } from './auth';

// Prefer an explicit backend base URL in local dev, but keep same-origin
// routing in production so Cloudflare Pages middleware can proxy requests.
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const API_V1_PREFIX = '/api/v1';

const buildApiUrl = (endpoint: string): string => {
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  if (!API_BASE_URL) {
    return normalizedEndpoint;
  }

  if (API_BASE_URL.endsWith(API_V1_PREFIX) && normalizedEndpoint.startsWith(`${API_V1_PREFIX}/`)) {
    return `${API_BASE_URL}${normalizedEndpoint.slice(API_V1_PREFIX.length)}`;
  }

  return `${API_BASE_URL}${normalizedEndpoint}`;
};

const createHttpError = (status: number, fallbackMessage: string) => {
  const message = `API Error: ${status}`;
  const error = new Error(message) as Error & {
    status: number;
    code?: string;
    isWorkerUnavailable?: boolean;
  };
  error.status = status;
  if (status === 503) {
    error.code = 'WORKER_UNAVAILABLE';
    error.isWorkerUnavailable = true;
    error.message = 'Worker service unavailable (503)';
  } else {
    error.message = fallbackMessage || message;
  }
  return error;
};

/**
 * API Client for Business Marketing Service
 * All endpoints require authentication
 */

class ApiService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth header with session token
    const sessionToken = authService.getSessionToken();
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }

    // Add business context header (required by JWTAuth when JWT has no embedded business_id)
    const businessId = authService.getBusinessId();
    if (businessId) {
      headers['X-Business-ID'] = businessId;
    }

    const response = await fetch(buildApiUrl(endpoint), {
      ...options,
      headers,
    });

    if (!response.ok) {
      let errorMsg = `API Error: ${response.status} ${response.statusText}`;
      let needReconnect = false;
      try {
        const body = await response.clone().json();
        if (body?.error) errorMsg = body.error;
        if (body?.need_reconnect) needReconnect = true;
      } catch {
        // ignore body parse errors
      }
      const err = createHttpError(response.status, errorMsg) as Error & {
        status: number;
        code?: string;
        isWorkerUnavailable?: boolean;
        needReconnect?: boolean;
      };
      err.needReconnect = needReconnect;
      throw err;
    }

    return response.json();
  }

  // Worker endpoints
  async getWorkers() {
    return this.request<any>('/api/v1/my-workers');
  }

  async getWorkerById(workerId: string) {
    return this.request<any>(`/api/v1/workers/${workerId}`);
  }

  // Worker Config endpoint
  async getWorkerConfig(workerUuid: string) {
    return this.request<any>(`/api/v1/configs/${workerUuid}?full=true`);
  }

  // Generic HTTP methods
  async get<T = any>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, data?: any) {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any) {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string) {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Account endpoints
  async getAccounts() {
    return this.request<any>('/api/v1/my-accounts');
  }

  async getAccountById(accountId: string) {
    return this.request<any>(`/api/v1/accounts/${accountId}`);
  }

  async createAccount(data: any) {
    return this.request<any>('/api/v1/accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAccount(accountId: string, data: any) {
    return this.request<any>(`/api/v1/accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async triggerVaultSync(action: 'upload' | 'download' = 'upload'): Promise<{ success: boolean; error?: string }> {
    return this.request<{ success: boolean; error?: string }>('/api/v1/vault/sync', {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  async deleteAccount(accountId: string) {
    return this.request<any>(`/api/v1/accounts/${accountId}`, {
      method: 'DELETE',
    });
  }

  // Platform stats
  async getPlatformStats() {
    return this.request<any>('/api/v1/stats/platforms');
  }

  // Campaign endpoints
  async getCampaigns() {
    return this.request<any>('/api/v1/campaigns');
  }

  async createCampaign(data: any) {
    return this.request<any>('/api/v1/campaigns', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaign(campaignId: string, data: any) {
    return this.request<any>(`/api/v1/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async assignAccountsToCampaign(campaignId: string, accountIds: string[]) {
    return this.request<any>(`/api/v1/campaigns/${campaignId}/assign-accounts`, {
      method: 'POST',
      body: JSON.stringify({ account_ids: accountIds }),
    });
  }

  // Task endpoints
  async getTasks() {
    return this.request<any>('/api/v1/tasks');
  }

  async getTaskById(taskId: string) {
    return this.request<any>(`/api/v1/tasks/${taskId}`);
  }

  async createTask(data: any) {
    return this.request<any>('/api/v1/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTask(taskId: string, data: any) {
    return this.request<any>(`/api/v1/tasks/${taskId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTask(taskId: string) {
    return this.request<any>(`/api/v1/tasks/${taskId}`, {
      method: 'DELETE',
    });
  }

  async updateTaskStatus(taskId: string, status: string, notes?: string) {
    return this.request<any>(`/api/v1/tasks/${taskId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, notes }),
    });
  }

  async completeTask(taskId: string, result: any) {
    return this.request<any>(`/api/v1/tasks/${taskId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ result }),
    });
  }

  async failTask(taskId: string, error: string) {
    return this.request<any>(`/api/v1/tasks/${taskId}/fail`, {
      method: 'POST',
      body: JSON.stringify({ error }),
    });
  }

  async cancelTask(taskId: string) {
    return this.request<any>(`/api/v1/tasks/${taskId}/cancel`, {
      method: 'POST',
    });
  }

  // Schedule endpoints - Get worker funnel URL, then frontend calls worker directly WITH AUTH
  async getWorkerScheduleURL(workerUuid: string) {
    return this.request<any>(`/api/v1/schedules/worker-url?worker_uuid=${workerUuid}`);
  }

  // Direct worker calls with JWT authentication from funnel link
  async getMasterCalendar(workerURL: string, jwtToken: string) {
    const response = await fetch(`${workerURL}/schedules/master`, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    });
    if (!response.ok) throw createHttpError(response.status, 'Failed to fetch master calendar');
    const data = await response.json();
    
    // Worker returns { success: true, calendar: {...} }
    // Extract calendar object from response
    if (!data.success || !data.calendar) {
      throw new Error(data.message || 'No schedules available');
    }
    
    return data.calendar;
  }

  async rebuildSchedules(workerURL: string, jwtToken: string) {
    const response = await fetch(`${workerURL}/schedules/rebuild`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    });
    if (!response.ok) throw createHttpError(response.status, 'Failed to rebuild schedules');
    const data = await response.json();
    
    // Worker returns { success: true, message: "...", result: {...} }
    if (!data.success) {
      throw new Error(data.message || 'Failed to rebuild');
    }
    
    return data;
  }

  // Report endpoints - Get worker tunnel URL, then frontend calls worker directly WITH AUTH
  async getWorkerReportURL(workerUuid: string) {
    return this.request<any>(`/api/v1/reports/worker-url?worker_uuid=${workerUuid}`);
  }

  // Direct worker calls for reports with JWT authentication
  async getReportsList(workerURL: string, jwtToken: string) {
    const response = await fetch(`${workerURL}/reports/list`, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    });
    if (!response.ok) throw createHttpError(response.status, 'Failed to fetch reports list');
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'No reports available');
    }
    
    return data;
  }

  async getAccountReports(workerURL: string, jwtToken: string, accountId: string, limit: number = 7) {
    const response = await fetch(`${workerURL}/reports/account/${accountId}?limit=${limit}`, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    });
    if (!response.ok) throw createHttpError(response.status, 'Failed to fetch account reports');
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'No reports available');
    }
    
    return data;
  }

  async getDailyReport(workerURL: string, jwtToken: string, accountId: string, date: string) {
    const response = await fetch(`${workerURL}/reports/account/${accountId}/${date}`, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    });
    if (!response.ok) throw createHttpError(response.status, 'Failed to fetch daily report');
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'Report not found');
    }
    
    return data.report;
  }

  async getDateRangeReports(workerURL: string, jwtToken: string, accountId: string, startDate: string, endDate?: string) {
    const url = endDate 
      ? `${workerURL}/reports/account/${accountId}/range?start_date=${startDate}&end_date=${endDate}`
      : `${workerURL}/reports/account/${accountId}/range?start_date=${startDate}`;
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
      },
    });
    if (!response.ok) throw createHttpError(response.status, 'Failed to fetch date range reports');
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.message || 'No reports available');
    }
    
    return data;
  }

  // ── Dashboard activity ── aggregated seeding stats by period (day/week/month)
  // Uses backend DB aggregation — no tunnel needed.
  async getDashboardActivity(workerUuid: string, period: 'day' | 'week' | 'month', timezone?: string) {
    const tz = timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.request<any>(
      `/api/v1/dashboard/activity?worker_uuid=${workerUuid}&period=${period}&timezone=${encodeURIComponent(tz)}`
    );
  }

  // ── Dashboard account stats ── pre-aggregated per-account daily task counters
  // Returns AccountDailyStat[] documents sorted by date asc.
  async getDashboardAccountStats(workerUuid: string, period: 'day' | 'week' | 'month') {
    return this.request<any>(
      `/api/v1/dashboard/account-stats?worker_uuid=${workerUuid}&period=${period}`
    );
  }
}

export const apiService = new ApiService();
export default apiService;
